import { Url as ReURL, RawUrl as ReRawURL } from 'reurl';
import { parseQuery, stringifyQuery } from '~/logic/helpers/url-query.js';

export const wrapQueryWithHTML = (text, { query = '', tag = 'mark', className = '', regexOptions = 'gi' }) => {
	if (!query) return text;
	
	// Escape special characters on query keyword RegExp errors
	const escapedCharsQuery = query.replace(/[-\\/\\^$*+?.()|[\]{}]/g, '\\$&');
	
	return text.replace(new RegExp(escapedCharsQuery, regexOptions), (match) => {
		return `<${tag} ${className ? `class="${className}"` : ''}>${match}</${tag}>`;
	});
};


/* 
	Takes in a URL string (could be relative), and ensure that it has .html extension.
	If it already has another extension (for e.g. .pdf), this function does nothing.
	Supports query params and hash params in the given string as well.
*/
export const ensureHtmlExtension = (urlString) => {
	if (!urlString) return '';
	
	let urlObj = new ReRawURL(urlString);
	
	if (urlObj.file) {
		if (urlObj.file.endsWith('.html') || urlObj.file.includes('.')) return urlString;
		
		urlObj = urlObj.set({
			file: `${urlObj.file}.html`,
		});
		return urlObj.toString().replaceAll('%20', '+');
	} else {
		// do not have "file", it might mean URL does not have pathname, just domain only
		return urlString;
	}
};

export const isUrlMatchCurrentHostname = (urlString) => {
	if (!urlString) return false;
	let urlProcessed = urlString;
	
	if (urlProcessed.startsWith('//')) urlProcessed = `${window.location.protocol}${urlProcessed}`;
	if (urlProcessed.startsWith('/')) return true;
	if (urlProcessed.startsWith('http') || urlProcessed.startsWith('https')) {
		try {
			const urlObj = new URL(urlProcessed);
			return urlObj.hostname === window.location.hostname;
		} catch {
			return false;
		}
	}
	if (urlProcessed.startsWith('file')) return false;
	
	return true;
};

export const addRetainingQueryToUrl = (url) => {
	const RETAINING_QUERIES = ['wcmmode', 'vite', 'empty', 'id'];
	const currentRouteQuery = window.router.currentRoute.value.query;
	const queryToAdd = {};
	
	// find the overlap in current url query, and the queries we want to append ('retain') to the url agrument
	for (const q of RETAINING_QUERIES) {
		if (q in currentRouteQuery) {
			queryToAdd[q] = currentRouteQuery[q];
		}
	}

	const _reUrl = new ReRawURL(url);
	const oriQuery = _reUrl.query ? parseQuery(_reUrl.query) : null;
	
	const finalQuery = {
		...queryToAdd,
		...oriQuery,
	};
	
	let _reUrl_queryMerged = _reUrl;
	if (Object.keys(finalQuery).length > 0) {
		_reUrl_queryMerged = _reUrl.set({ query: stringifyQuery(finalQuery) });
	}
	return _reUrl_queryMerged.toString().replaceAll('%20', '+');
};

export const formatNumberWithAbbreviation = (num, digitLengthStartAbbreviate) => {
	if (!num) return '';

	const numStr = num.toString();

	// If number has digitLengthStartAbbreviate or fewer characters, return as is
	if (digitLengthStartAbbreviate && numStr.length <= digitLengthStartAbbreviate) {
		return numStr;
	}

	if (num >= 1000000000000) {
		return formatNumber(num, 1000000000000, 't');
	} else if (num >= 1000000000) {
		return formatNumber(num, 1000000000, 'b');
	} else if (num >= 1000000) {
		return formatNumber(num, 1000000, 'm');
	} else if (num >= 1000) {
		return formatNumber(num, 1000, 'k');
	}

	return Math.round(parseFloat(num)).toString();
	
};

const formatNumber = (num, divisor, suffix) => {
	const result = (num / divisor).toFixed(2);
	return result.endsWith('.00') ? `${result.slice(0, -3)}${suffix}` : `${result}${suffix}`;
};

export const formatNumberWithComma = (number) => {
	if (!number) return '';
	const numStr = number.toString();
	const parts = numStr.split('.');
	parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	return parts.join('.');
};

export const lowFarePriceNumberFormat = (num) => {
	if (!num) return '';

	if (num >= 1000000000000) {
		return formatNumber(num, 1000000000000, 't');
	} else if (num >= 1000000000) {
		return formatNumber(num, 1000000000, 'b');
	} else if (num >= 1000000) {
		return formatNumber(num, 1000000, 'm');
	} else if (num >= 10000 && num >= 100000) {
		return `${Math.floor(num / 1000)}k`;
	} else if (num >= 1000) {
		let number = Math.round(num.endsWith('.00') ? `${num.slice(0, -3)}` : `${num}`);
		return formatNumberWithComma(number);
	}
	return Math.round(parseFloat(num)).toString();
};

/**
 * Formats a number by flooring it to two decimal places and returns it as a string.
 * 
 * @param {number} num - The number to be formatted.
 * @returns {string} The formatted number string with exactly two decimal places.
 * 
 * @example
 * formatNumberFloorToTwoDecimals(123.456) // returns "123.45"
 * @example
 * formatNumberFloorToTwoDecimals(123.4)   // returns "123.40"
 * @example
 * formatNumberFloorToTwoDecimals(123)     // returns "123.00"
 */
export const formatNumberFloorToTwoDecimals = (num) => {
	return (Math.floor(num * 100) / 100).toFixed(2);
};

export const lowPriceNumFormatForKorean = (num) => {
	if (!num) return '';
	let value = Math.round(num);
	const number = Number(value);
	if (value < 10000) {
		return value;
	}
	const result = number / 10000;
	return result.toLocaleString('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}) + '만';
};

// Rounds to nearest integer and formats with thousand separators.
// Accepts number or numeric string. Returns '' for null/undefined/invalid.
export const formatRoundedIntegerWithCommas = (value) => {
	if (value === null || value === undefined) return '';
	const num = Number(String(value).trim());
	if (Number.isNaN(num)) return '';
	const rounded = Math.round(num);
	const absStr = Math.abs(rounded).toString();
	const withCommas = absStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	return rounded < 0 ? `-${withCommas}` : withCommas;
};


